import { GraphicPanel } from "./graphic-panel";
import { WebglObject } from "./webgl-object";
import { FixedDimension } from "../utils/fixed-dimension";

export class GraphicColumnGrid extends WebglObject {
  /*
  settings: {
    columnsSpace: new ScalableDimension(0, 0),
    columns: [
      {
        width: new ScalableDimension(0, 0),
        rowsSpace: new ScalableDimension(0, 0),
        rows: [
          {
            height: new ScalableDimension(0, 0)
          }
        ]
      }
    ]
  } 
  */
  constructor(settings) {
    super(settings);

    let commonSpaceBetweenColumns = 0;
    const columnsSpaceInPixels = this.settings.columnsSpace.calcValue(this.settings.clientSize.width);
    if (this.settings.columns.length > 1) {
      commonSpaceBetweenColumns = columnsSpaceInPixels * (this.settings.columns.length - 1);
    }
    const panelsAreaWidth = this.settings.clientSize.width - commonSpaceBetweenColumns;
    let cellX = 0;
    for (let columnIndex = 0; columnIndex < this.settings.columns.length; columnIndex++) {
      const columnInfo = this.settings.columns[columnIndex];
      const columnWidth = columnInfo.width.calcValue(panelsAreaWidth);
      const rowsSpaceInPixels = columnInfo.rowsSpace.calcValue(this.settings.clientSize.height);
      let cellY = 0;
      let commonSpaceBetweenRows = 0;
      if (columnInfo.rows.length > 1) {
        commonSpaceBetweenRows = rowsSpaceInPixels * (columnInfo.rows.length - 1);
      }
      const panelsAreaHeight = this.settings.clientSize.height - commonSpaceBetweenRows;
      for (let rowIndex = 0; rowIndex < columnInfo.rows.length; rowIndex++) {
        const rowInfo = columnInfo.rows[rowIndex];
        const rowHeight = rowInfo.height.calcValue(panelsAreaHeight);
        rowInfo.panel = new GraphicPanel(
          {
            location: {
              x: new FixedDimension(cellX),
              y: new FixedDimension(this.settings.clientSize.height - cellY - rowHeight)
            },
            size: {
              width: columnInfo.width,
              height: rowInfo.height
            },
            parent: {
              ref: this,
              isCustom: true,
              location: {
                x: this.settings.absoluteLocation.x,
                y: this.settings.absoluteLocation.y
              },
              size: {
                width: panelsAreaWidth,
                height: panelsAreaHeight
              },
              padding: {
                left: this.settings.paddingInPixels.left,
                right: this.settings.paddingInPixels.right,
                top: this.settings.paddingInPixels.top,
                bottom: this.settings.paddingInPixels.bottom
              }
            }
          }
        );
        cellY += rowHeight + rowsSpaceInPixels;
      }
      cellX += columnWidth + columnsSpaceInPixels;
    }
    this.settings.debug.boxFillColor = [0.0, 0.8, 0, 1];
    this.settings.debug.boxStrokeColor = [0, 0.2, 0, 1];
  }

  createBorders() {
    super.createBorders();
    let commonSpaceBetweenColumns = 0;
    const columnsSpaceInPixels = this.settings.columnsSpace.calcValue(this.settings.clientSize.width);
    if (this.settings.columns.length > 1) {
      commonSpaceBetweenColumns = columnsSpaceInPixels * (this.settings.columns.length - 1);
    }
    const panelsAreaWidth = this.settings.clientSize.width - commonSpaceBetweenColumns;
    let cellX = 0;
    for (let columnIndex = 0; columnIndex < this.settings.columns.length; columnIndex++) {
      const columnInfo = this.settings.columns[columnIndex];
      const columnWidth = columnInfo.width.calcValue(panelsAreaWidth);
      const rowsSpaceInPixels = columnInfo.rowsSpace.calcValue(this.settings.clientSize.height);
      let commonSpaceBetweenRows = 0;
      if (columnInfo.rows.length > 1) {
        commonSpaceBetweenRows = rowsSpaceInPixels * (columnInfo.rows.length - 1);
      }
      const panelsAreaHeight = this.settings.clientSize.height - commonSpaceBetweenRows;
      let cellY = 0;
      for (let rowIndex = 0; rowIndex < columnInfo.rows.length; rowIndex++) {
        const rowInfo = columnInfo.rows[rowIndex];
        const rowHeight = rowInfo.height.calcValue(panelsAreaHeight);
        rowInfo.panel.setLocation(new FixedDimension(cellX), new FixedDimension(this.settings.clientSize.height - cellY - rowHeight));
        rowInfo.panel.setParentSize(panelsAreaWidth, panelsAreaHeight);
        rowInfo.panel.createBorders();
        cellY += rowHeight + rowsSpaceInPixels;
      }
      cellX += columnWidth + columnsSpaceInPixels;
    }
  }

  setRowHeight(rowIndex, columnIndex, height) {
    if (
      columnIndex < 0 ||
      columnIndex >= this.settings.columns.length ||
      rowIndex < 0 ||
      rowIndex >= this.settings.columns[columnIndex].rows.length
    ) {
      return;
    }
    const columnInfo = this.settings.columns[columnIndex];
    let cellInfo = columnInfo.rows[rowIndex];
    cellInfo.height = height;
    cellInfo.panel.setSize(cellInfo.panel.settings.size.width, height);

    let commonSpaceBetweenRows = 0;
    const rowsSpaceInPixels = cellInfo.rowsSpace.calcValue(this.settings.clientSize.height);
    if (cellInfo.rows.length > 1) {
      commonSpaceBetweenRows = rowsSpaceInPixels * (cellInfo.rows.length - 1);
    }
    const panelsAreaHeight = this.settings.clientSize.height - commonSpaceBetweenRows;

    const cellX = cellInfo.panel.settings.locationInPixels.x;
    let cellY = this.settings.clientSize.height - cellInfo.panel.settings.locationInPixels.y + rowsSpaceInPixels;
    for (let r = rowIndex + 1; r < columnInfo.rows.length; r++) {
      cellInfo = columnInfo.rows[r];
      const rowHeight = cellInfo.height.calcValue(panelsAreaHeight);
      cellInfo.panel.setLocation(new FixedDimension(cellX), new FixedDimension(this.settings.clientSize.height - cellY - rowHeight));
      cellY += rowHeight + rowsSpaceInPixels;
    }
  }

  setColumnWidth(columnIndex, width) {
    if (columnIndex < 0 || columnIndex >= this.settings.columns.length) {
      return;
    }
    let selectedColumnInfo = this.settings.columns[columnIndex];
    selectedColumnInfo.width = width;
    for (let rowIndex = 0; rowIndex < selectedColumnInfo.rows.length; rowIndex++) {
      let cellInfo = selectedColumnInfo.rows[rowIndex];
      cellInfo.panel.setSize(width, cellInfo.panel.settings.size.height);

      let commonSpaceBetweenColumns = 0;
      const columnsSpaceInPixels = this.settings.columnsSpace.calcValue(this.settings.clientSize.width);
      if (this.settings.columns.length > 1) {
        commonSpaceBetweenColumns = columnsSpaceInPixels * (this.settings.columns.length - 1);
      }
      const panelsAreaWidth = this.settings.clientSize.width - commonSpaceBetweenColumns;

      const columnWidth = selectedColumnInfo.width.calcValue(panelsAreaWidth);
      let cellX = cellInfo.panel.settings.locationInPixels.x + columnWidth + columnsSpaceInPixels;
      for (let colIndex = columnIndex + 1; colIndex < this.settings.columns.length; colIndex++) {
        const columnInfo = this.settings.columns[colIndex];
        let commonSpaceBetweenRows = 0;
        const rowsSpaceInPixels = columnInfo.rowsSpace.calcValue(this.settings.clientSize.height);
        if (columnInfo.rows.length > 1) {
          commonSpaceBetweenRows = rowsSpaceInPixels * (columnInfo.rows.length - 1);
        }
        const panelsAreaHeight = this.settings.clientSize.height - commonSpaceBetweenRows;
        let cellY = 0;
        for (let rowIndex = 0; rowIndex < columnInfo.rows.length; rowIndex++) {
          cellInfo = columnInfo.rows[rowIndex];
          const rowHeight = cellInfo.height.calcValue(panelsAreaHeight);
          cellInfo.panel.setLocation(new FixedDimension(cellX), new FixedDimension(this.settings.clientSize.height - cellY - rowHeight));
          cellY += rowHeight + rowsSpaceInPixels;
        }
        cellX += columnInfo.width.calcValue(panelsAreaWidth) + columnsSpaceInPixels;
      }
    }
  }

  addItem(columnIndex, rowIndex, item) {
    const panel = this.settings.columns[columnIndex].rows[rowIndex].panel;
    panel.addItem(item);
  }

  setDebugMode(debugSettings) {
    super.setDebugMode(debugSettings);
    for (let columnIndex = 0; columnIndex < this.settings.columns.length; columnIndex++) {
      const rows = this.settings.columns[columnIndex].rows;
      for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
        rows[rowIndex].panel.setDebugMode(debugSettings);
      }
    }
  }

  draw(gl, program) {
    super.draw(gl, program);

    for (let columnIndex = 0; columnIndex < this.settings.columns.length; columnIndex++) {
      const rows = this.settings.columns[columnIndex].rows;
      for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
        rows[rowIndex].panel.draw(gl, program);
      }
    }
  }
}
